import React from 'react';
import {
  Alert,
  Box,
  Button,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';

import TrendsArchivePage from './TrendsArchivePage';
import TrendsHistoryPage from './TrendsHistoryPage';
import TrendsSourcesReaderPage from './TrendsSourcesReaderPage';
import { DISPLAY_FONT, TREND_COLORS, PAGE_HEADING_SX } from './trendTheme';

import { TrendAdminProvider, useTrendAdmin } from './controlCenter/TrendAdminContext';
import BrandRadarTab from './controlCenter/tabs/BrandRadarTab';
import SourcesTab from './controlCenter/tabs/SourcesTab';
import StatusTab from './controlCenter/tabs/StatusTab';
import SettingsTab from './controlCenter/tabs/SettingsTab';
import DiscoveryTab from './controlCenter/tabs/DiscoveryTab';
import GuideTab from './controlCenter/tabs/GuideTab';
import SourceFormDialog from './controlCenter/dialogs/SourceFormDialog';
import DiscoveryQueryDialog from './controlCenter/dialogs/DiscoveryQueryDialog';
import ResetDialog from './controlCenter/dialogs/ResetDialog';
import ArchiveDialog from './controlCenter/dialogs/ArchiveDialog';

type TrendsSourceAdminPageProps = {
  canAdmin?: boolean;
  canReview?: boolean;
  canManageSources?: boolean;
  navigateToView?: (view: 'overview' | 'editorial') => void;
};

function TrendsSourceAdminPageView() {
  const {
    error,
    actionError,
    setActionError,
    message,
    setMessage,
    activeTab,
    setActiveTab,
    activeRun,
    latestRun,
  } = useTrendAdmin();
  const [dismissedFailedRunId, setDismissedFailedRunId] = React.useState<string | null>(null);
  const latestFailedRunId = latestRun?.status === 'failed' && latestRun.error ? latestRun._id : null;

  const tabGroupSx = {
    minHeight: 44,
    '& .MuiTab-root': { fontFamily: DISPLAY_FONT, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, fontSize: '0.85rem', minHeight: 44, py: 1 },
    '& .Mui-selected': { color: TREND_COLORS.navy },
    '& .MuiTabs-indicator': { bgcolor: TREND_COLORS.navy },
  };

  const renderTabGroup = (label: string, items: { value: number; label: string }[]) => {
    const groupValue = items.some((i) => i.value === activeTab) ? activeTab : false;
    return (
      <Box>
        <Typography
          sx={{
            display: 'block', pl: 1.5, mb: 0.5,
            fontFamily: DISPLAY_FONT, textTransform: 'uppercase', letterSpacing: '0.08em',
            fontSize: '0.8rem', fontWeight: 800, color: TREND_COLORS.navy,
          }}
        >
          {label}
        </Typography>
        <Tabs
          value={groupValue}
          onChange={(_e, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons={false}
          sx={tabGroupSx}
        >
          {items.map((i) => <Tab key={i.value} value={i.value} label={i.label} />)}
        </Tabs>
      </Box>
    );
  };

  return (
    <Stack spacing={3} sx={{ maxWidth: 1200, mx: 'auto' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
        <Typography sx={{ ...PAGE_HEADING_SX, fontSize: '1.8rem' }}>KONTROLLCENTER</Typography>
        <Button
          size="small"
          variant={activeTab === 8 ? 'contained' : 'outlined'}
          startIcon={<MenuBookOutlinedIcon />}
          onClick={() => setActiveTab(8)}
          sx={{
            fontFamily: DISPLAY_FONT, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600,
            ...(activeTab === 8
              ? { bgcolor: TREND_COLORS.navy, '&:hover': { bgcolor: TREND_COLORS.navy } }
              : { color: TREND_COLORS.navy, borderColor: TREND_COLORS.divider }),
          }}
        >
          Så fungerar det
        </Button>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}
      {actionError && <Alert severity="error" onClose={() => setActionError(null)}>{actionError}</Alert>}
      {!activeRun && latestFailedRunId && dismissedFailedRunId !== latestFailedRunId && (
        <Alert severity="error" onClose={() => setDismissedFailedRunId(latestFailedRunId)}>
          Senaste körningen misslyckades: {latestRun.error}
        </Alert>
      )}
      {message && <Alert severity="success" onClose={() => setMessage(null)}>{message}</Alert>}

      <Box sx={{ borderBottom: `1px solid ${TREND_COLORS.divider}`, display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', columnGap: 4, rowGap: 1.5 }}>
        {renderTabGroup('Drift', [
          { value: 0, label: 'Status & Körning' },
          { value: 2, label: 'Discovery' },
          { value: 3, label: 'Brand Radar' },
        ])}
        {renderTabGroup('Konfiguration', [
          { value: 1, label: 'Källor' },
          { value: 7, label: 'Inställningar & Profil' },
        ])}
        {renderTabGroup('Insyn & arkiv', [
          { value: 4, label: 'Hämtat material' },
          { value: 5, label: 'Historik' },
          { value: 6, label: 'Rapportarkiv' },
        ])}
      </Box>

      {activeTab === 0 && <StatusTab />}
      {activeTab === 1 && <SourcesTab />}
      {activeTab === 2 && <DiscoveryTab />}
      {activeTab === 3 && <BrandRadarTab />}
      {activeTab === 4 && <TrendsSourcesReaderPage />}
      {activeTab === 5 && <TrendsHistoryPage />}
      {activeTab === 6 && <TrendsArchivePage />}
      {activeTab === 7 && <SettingsTab />}
      {activeTab === 8 && <GuideTab />}

      <SourceFormDialog />

      <DiscoveryQueryDialog />

      <ResetDialog />

      <ArchiveDialog />
    </Stack>
  );
}

export default function TrendsSourceAdminPage({
  canAdmin = false,
  canReview = false,
  canManageSources = true,
  navigateToView,
}: TrendsSourceAdminPageProps) {
  return (
    <TrendAdminProvider
      canAdmin={canAdmin}
      canReview={canReview}
      canManageSources={canManageSources}
      navigateToView={navigateToView}
    >
      <TrendsSourceAdminPageView />
    </TrendAdminProvider>
  );
}
